import BigNumber from 'bignumber.js';

import type { PointType } from '@infinex/asset-config';

import {
  createRafflePrizeKey,
  getNonSparePrizes,
  type RafflePrize,
} from './prizes';
import type { RaffleRuleVersion } from './versions';
import { getRaffleVersionConfig } from './versions';

export { createRafflePrizeKey };

export const RAFFLE_TYPES = ['periodic', 'milestone'] as const;
export type RaffleType = (typeof RAFFLE_TYPES)[number];

export const RAFFLE_PERIODS = ['weekly', 'monthly', 'daily'] as const;
export type RafflePeriod = (typeof RAFFLE_PERIODS)[number];

export const PERIODIC_RAFFLE_TAGS = ['cash', 'patron'] as const;
export type PeriodicRaffleTag = (typeof PERIODIC_RAFFLE_TAGS)[number];

//-------------------------------------
// Types
//-------------------------------------
export type Raffle = {
  id: number;
  drawRuleVersion: RaffleRuleVersion;
  label: string;
  description: string | null;
  isDrawn: boolean;
  opensAt: string | Date;
  admitableAtOffsetMinutes: number;
  ticketDenominationId: string;
  randomSeed: string | null;
  drawRecords: RaffleDrawRecord[] | null;
  totalTickets: number | null;
  holderCount: number | null;
  drawnAt: string | Date | null;
  isPublished: boolean;
} & (
  | {
      kind: 'periodic';
      period: RafflePeriod;
      tag: PeriodicRaffleTag;
      closesAtOffsetMinutes: number;
    }
  | {
      kind: 'milestone';
      drawCriteria: MilestoneRaffleDrawCriteria;
      currentProgress: number;
    }
);

export type MilestoneRaffleDrawCriteriaType = 'volume' | 'crates_opened';

export type MilestoneRaffleDrawCriteria = {
  type: MilestoneRaffleDrawCriteriaType;
  threshold: number;
};

export type RaffleWin = {
  id: string;
  prizeDrawId: number;
  prizeDrawType: RaffleType;
  userId: string;
  prizes: RafflePrize[];
  description: string | null;
  ticketsHeld: number;
  position: number;
  txHash: string | null;
  fulfilledAt: Date | null;
  createdAt: Date;
};

export type RaffleWinner = {
  prizes: RafflePrize[];
  position: number; // 1st, 2nd, 3rd place for multi-prize raffles
  userId: string;
  ticketsHeld: number;
  isSpare: boolean;
};

export type RaffleWinnerIndex = {
  position: number;
  ticketIndex: number;
  prizes: RafflePrize[];
};

export type RaffleDrawRecord = {
  index: number;
  position: number;
  isDiscarded: boolean;
  ticketIndex: number;
  ticketsHeld: number;
  userId: string;
  nerfFactor: number;
};

export type PeriodicRaffle = Extract<Raffle, { kind: 'periodic' }>;
export type MilestoneRaffle = Extract<Raffle, { kind: 'milestone' }>;

//-------------------------------------
// Helpers
//-------------------------------------

export function getPeriodicRaffleTimes({
  opensAt,
  admitableAtOffsetMinutes,
  closesAtOffsetMinutes,
}: PeriodicRaffle) {
  const opensAtMs = new Date(opensAt).getTime();
  const admitableAtMs = opensAtMs - admitableAtOffsetMinutes * 60 * 1000;
  const closesAtMs = opensAtMs + closesAtOffsetMinutes * 60 * 1000;

  return {
    opensAtMs,
    admitableAtMs,
    closesAtMs,
  };
}

export function getRaffleCloseTime(raffle: Raffle): number | null {
  switch (raffle.kind) {
    case 'periodic':
      return getPeriodicRaffleTimes(raffle).closesAtMs;
    case 'milestone':
      if (!raffle.drawnAt) {
        return null;
      }
      return new Date(raffle.drawnAt).getTime();
  }
}

export function sortUpcomingRaffles(
  raffles: Raffle[],
  isOpen = true
): Raffle[] {
  const rafflesToSort = isOpen
    ? raffles.filter((raffle) => !raffle.isDrawn)
    : raffles;

  return rafflesToSort.sort((a, b) => {
    // milestone raffles are always at top
    if (a.kind === 'milestone' && b.kind !== 'milestone') {
      return -1;
    }
    if (a.kind !== 'milestone' && b.kind === 'milestone') {
      return 1;
    }
    return (
      getPeriodicRaffleTimes(a as PeriodicRaffle).opensAtMs -
      getPeriodicRaffleTimes(b as PeriodicRaffle).opensAtMs
    );
  });
}

export function sortPastRaffles(raffles: Raffle[]): Raffle[] {
  return raffles.sort(
    (a, b) => (getRaffleCloseTime(a) ?? 0) - (getRaffleCloseTime(b) ?? 0)
  );
}

export function getPeriodicRaffleState({
  now = new Date(),
  ...periodicRaffle
}: PeriodicRaffle & { now?: Date | string }) {
  const nowMs = new Date(now).getTime();
  const times = getPeriodicRaffleTimes(periodicRaffle);

  const { isDrawn } = periodicRaffle;
  const { opensAtMs, admitableAtMs, closesAtMs } = times;

  const isAdmittable = nowMs > admitableAtMs;
  const isClosed = nowMs > closesAtMs;
  const isOpen = !isClosed && nowMs > opensAtMs;

  return {
    times,
    isDrawn,
    isAdmittable,
    isOpen,
    isClosed,
  };
}

type AggregatedRafflePrize = Omit<RafflePrize, 'decimalAmount'> & {
  decimalAmount: BigNumber;
};

export const aggregateRafflePrizes = (
  rafflePrizes: RafflePrize[][]
): RafflePrize[] => {
  const totals = new Map<string, AggregatedRafflePrize>();
  for (const prizes of rafflePrizes) {
    for (const prize of prizes) {
      const key = createRafflePrizeKey(prize);
      const total = totals.get(key);
      totals.set(key, {
        ...prize,
        decimalAmount: total
          ? total.decimalAmount.plus(prize.decimalAmount)
          : new BigNumber(prize.decimalAmount),
      });
    }
  }
  return Array.from(totals.values()).map((total) => ({
    ...total,
    decimalAmount: total.decimalAmount.toString(),
  })) as RafflePrize[];
};

//-------------------------------------
// Progress & Display Helpers
//-------------------------------------

export type RaffleProgress = {
  totalDuration?: number;
  elapsedMs: number;
  percent: number;
  unit: 'money' | 'amount' | 'time';
};

/**
 * Calculate progress for periodic (weekly) raffles based on time elapsed
 */
export const getPeriodicRaffleProgress = (
  raffle: PeriodicRaffle,
  now: Date = new Date()
): RaffleProgress => {
  const { opensAtMs, closesAtMs } = getPeriodicRaffleTimes(raffle);
  const nowMs = now.getTime();

  const totalDuration = closesAtMs - opensAtMs;
  const elapsed = Math.max(0, Math.min(nowMs - opensAtMs, totalDuration));
  const percent = Math.min(100, (elapsed / totalDuration) * 100);

  const elapsedMs = Math.max(0, nowMs - opensAtMs);

  return {
    totalDuration,
    elapsedMs,
    percent,
    unit: 'time',
  };
};

/**
 * Calculate progress for milestone raffles
 */
export const getMilestoneRaffleProgress = (
  raffle: MilestoneRaffle
): RaffleProgress => {
  const { currentProgress, drawCriteria } = raffle;

  return {
    elapsedMs: 0,
    percent: currentProgress,
    unit: drawCriteria.type === 'volume' ? 'money' : 'amount',
  };
};

export type RafflePrizeDisplayVars =
  | {
      decimalAmount: string;
      symbol: string;
      quantity: number;
      isOther: boolean;
    }
  | {
      label: string;
      quantity: number;
      isOther: boolean;
    }
  | {
      decimalAmount: string;
      pointType: PointType;
      quantity: number;
      isOther: boolean;
    };

/**
 * Format raffle prize display text based on rule version
 */
export const getRafflePrizeDisplayVars = (raffle: {
  drawRuleVersion: RaffleRuleVersion;
}): RafflePrizeDisplayVars => {
  const rules = getRaffleVersionConfig(raffle.drawRuleVersion);
  const prizes = getNonSparePrizes(rules);
  const prizeCount = Object.keys(prizes).length;
  const areAllPrizesSame = Object.values(prizes).every(
    (prizeGroup) =>
      prizeGroup.map(createRafflePrizeKey).join('-') ===
      prizes[1].map(createRafflePrizeKey).join('-')
  );

  // Get first prize to determine type
  const firstPrize = prizes[1]?.[0];

  if (!firstPrize)
    return { label: 'Unknown Prize', quantity: 1, isOther: false };

  if (firstPrize.type === 'TOKEN') {
    return {
      decimalAmount: firstPrize.decimalAmount,
      symbol: firstPrize.name,
      quantity: areAllPrizesSame ? prizeCount : 1,
      isOther: !areAllPrizesSame,
    };
  }

  if (firstPrize.type === 'SPECIAL') {
    return {
      label: firstPrize.name,
      quantity: areAllPrizesSame ? prizeCount : 1,
      isOther: !areAllPrizesSame,
    };
  }

  if (firstPrize.type === 'POINTS') {
    return {
      decimalAmount: firstPrize.decimalAmount,
      pointType: firstPrize.pointType,
      quantity: areAllPrizesSame ? prizeCount : 1,
      isOther: !areAllPrizesSame,
    };
  }

  return {
    label: 'Prize Pool',
    quantity: 1,
    isOther: false,
  };
};

/**
 * get the drawn date for a raffle
 */
export const getRaffleDrawnDate = (raffle: Raffle): Date | null => {
  if (raffle.kind === 'periodic') {
    return new Date(
      getPeriodicRaffleTimes(raffle as PeriodicRaffle).closesAtMs
    );
  }
  if (!raffle.drawnAt) {
    return null;
  }
  return new Date(raffle.drawnAt);
};

export const isRaffleOpen = (raffle: Raffle): boolean => {
  if (raffle.kind === 'periodic') {
    return getPeriodicRaffleState(raffle as PeriodicRaffle).isOpen;
  }
  return new Date(raffle.opensAt).getTime() < new Date().getTime();
};

export const sortRafflesByDrawCriteria = (
  raffles: MilestoneRaffle[],
  descending = true
): MilestoneRaffle[] => {
  return raffles.sort((a, b) =>
    descending
      ? Number(b.drawCriteria.threshold) - Number(a.drawCriteria.threshold)
      : Number(a.drawCriteria.threshold) - Number(b.drawCriteria.threshold)
  );
};

export const doesRaffleWinnerNeedToContactUs = (
  ruleVersion: RaffleRuleVersion
): boolean => {
  const rules = getRaffleVersionConfig(ruleVersion);
  return rules.needsToContactUs;
};

export const createRaffleMap = (raffles: Raffle[]) => {
  return raffles.reduce((acc, raffle) => {
    acc.set(createRaffleKey(raffle.id, raffle.kind), raffle);
    return acc;
  }, new Map<string, Raffle>());
};

export const createRaffleKey = (raffleId: number, raffleKind: RaffleType) => {
  return `${raffleKind}_${raffleId}`;
};

//-------------------------------------
// Raffle Win Display Helpers
//-------------------------------------
/**
 * All relevant image URLs for displaying a raffle win
 */
export type RaffleWinDisplayInfo = {
  /** Banner image for the win dialog header */
  bannerUrl: string;
  /** Icon for the raffle (shown in cards/banners) */
  iconUrl: string;
  /** Icon for the first prize won */
  prizeIconUrl: string;
  /** Name of the first prize */
  prizeName: string;
  /** Description of all prizes */
  prizeDescription: string;
};

/**
 * Get all relevant display info for a raffle win.
 * Useful for rendering win dialogs and banners.
 */
export const getRaffleWinDisplayInfo = (
  ruleVersion: RaffleRuleVersion
): RaffleWinDisplayInfo => {
  const config = getRaffleVersionConfig(ruleVersion);
  const prize = Object.values(config.prizes)[0]?.[0];
  if (!prize) {
    throw new Error(`No prizes configured for raffle version: ${ruleVersion}`);
  }
  return {
    bannerUrl: config.bannerUrl,
    iconUrl: config.iconUrl,
    prizeIconUrl: prize.iconUrl,
    prizeName: prize.name,
    prizeDescription: config.prizeDescription,
  };
};
